import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import cyclingService from '../../services/cyclingService'
import { ConfirmModal } from '../../components/ConfirmModal'
import { formatDateTime } from '../../utils/formatters'

export const CyclingPage = () => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const now = new Date()
  const previousYear = now.getFullYear() - 1

  // State
  const [selectedYear, setSelectedYear] = useState(previousYear)
  const [showApplyModal, setShowApplyModal] = useState(false)

  // Fetch preview
  const { data: preview, isLoading: isLoadingPreview, error: previewError } = useQuery({
    queryKey: ['cycling-preview', selectedYear],
    queryFn: () => cyclingService.preview(selectedYear),
    retry: 1,
  })

  // Fetch cycling history
  const { data: history = [], isLoading: isLoadingHistory } = useQuery({
    queryKey: ['cycling-history'],
    queryFn: () => cyclingService.getAll(),
  })

  // Apply mutation
  const applyMutation = useMutation({
    mutationFn: () => cyclingService.apply(selectedYear),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycling-preview'] })
      queryClient.invalidateQueries({ queryKey: ['cycling-history'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      queryClient.invalidateQueries({ queryKey: ['quarterly-summary'] })
      setShowApplyModal(false)
      toast.success('Ciclagem aplicada com sucesso! Pontos foram reduzidos em 50%.')
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao aplicar ciclagem.'
      toast.error(message)
    },
  })

  // Check if year already applied
  const isYearApplied = history.some((h) => h.reference_year === selectedYear)

  // Safe defaults - works with 0 or thousands of records
  const previewEmployees = preview?.employees || []
  const totalEmployees = preview?.total_employees || 0

  // Year options (current - 1 and current - 2)
  const yearOptions = [now.getFullYear() - 1, now.getFullYear() - 2]

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        Ciclagem Anual
      </h1>

      {/* Warning Banner */}
      <div className="bg-warning/10 border border-warning/20 rounded-card p-6 mb-6">
        <div className="flex items-start gap-3">
          <span className="text-3xl">⚠️</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Atenção: Operação Irreversível
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              A ciclagem anual aplica um <strong>decay de 50%</strong> nos pontos acumulados
              do ano anterior. Esta operação:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>Reduz todos os pontos do ano selecionado pela metade</li>
              <li>Cria um snapshot JSON permanente de todos os funcionários</li>
              <li>É registrada no log de auditoria</li>
              <li><strong>Não pode ser desfeita após aplicação</strong></li>
              <li>Só pode ser aplicada uma vez por ano</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Year Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ano de Referência
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={applyMutation.isPending}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year} (ano anterior: {year})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              A ciclagem só pode ser aplicada no ano imediatamente anterior ao atual.
            </p>
          </div>

          {/* Apply Button */}
          {preview && !isYearApplied && (
            <div className="pt-6">
              <button
                onClick={() => setShowApplyModal(true)}
                disabled={applyMutation.isPending}
                className="px-6 py-3 bg-danger hover:bg-danger-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Aplicar Ciclagem
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Already Applied Warning */}
      {isYearApplied && (
        <div className="bg-success/10 border border-success/20 rounded-card p-6 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Ciclagem já aplicada para {selectedYear}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                O decay de 50% já foi aplicado neste ano. Não é possível aplicar novamente.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Error */}
      {previewError && (
        <div className="bg-danger/10 border border-danger/20 rounded-card p-6 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">❌</span>
            <div>
              <h3 className="text-lg font-semibold text-danger">
                Erro ao carregar prévia
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {(previewError as any).response?.data?.detail || 'Não foi possível carregar a prévia da ciclagem.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Loading */}
      {isLoadingPreview && !isYearApplied && (
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando prévia...</p>
        </div>
      )}

      {/* Preview Table */}
      {preview && !isYearApplied && !isLoadingPreview && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Prévia da Ciclagem
            </h2>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {totalEmployees} funcionários afetados
            </span>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Funcionário
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Pontos Antes
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Decay (50%)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Pontos Depois
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {previewEmployees.map((employee) => (
                    <tr key={employee.employee_id} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {employee.employee_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {employee.points_before}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-danger">
                          -{employee.decay_amount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-primary">
                          {employee.points_after}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Histórico de Ciclagens
        </h2>

        {isLoadingHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-card shadow p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Carregando histórico...</p>
          </div>
        )}

        {!isLoadingHistory && history.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-card shadow p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Nenhuma ciclagem aplicada ainda
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Quando uma ciclagem for aplicada, ela aparecerá aqui.
            </p>
          </div>
        )}

        {!isLoadingHistory && history.length > 0 && (
          <div className="space-y-4">
            {history.map((cycling) => (
              <div key={cycling.id} className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Ciclagem {cycling.reference_year}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Aplicada em {formatDateTime(cycling.applied_at)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {cycling.snapshot.length} funcionários afetados
                    </p>
                  </div>
                  <div className="text-4xl">
                    🔄
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Apply Confirmation Modal */}
      <ConfirmModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onConfirm={() => applyMutation.mutate()}
        title="⚠️ Confirmar Ciclagem Anual"
        description={
          <div className="space-y-3">
            <p className="font-semibold text-danger">
              ATENÇÃO: Esta operação é IRREVERSÍVEL!
            </p>
            <p>
              Você está prestes a aplicar o decay de <strong>50%</strong> nos pontos de <strong>{preview?.total_employees} funcionários</strong> referentes ao ano <strong>{selectedYear}</strong>.
            </p>
            <p className="text-sm">
              <strong>O que vai acontecer:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Todos os pontos do ano {selectedYear} serão reduzidos pela metade</li>
              <li>Um snapshot permanente será criado</li>
              <li>A operação será registrada no log de auditoria</li>
              <li>Não será possível desfazer esta ação</li>
            </ul>
            <p className="text-sm font-medium">
              Tem certeza que deseja continuar?
            </p>
          </div>
        }
        confirmText="Sim, Aplicar Ciclagem"
        cancelText="Cancelar"
        isDanger={true}
        isLoading={applyMutation.isPending}
      />
    </div>
  )
}

export default CyclingPage
